import React from 'react';
import { uniqueId } from 'lodash';
import { FieldArrayRenderProps, Field, FieldProps, useFormikContext } from 'formik';
import { FormGroupLabel, Dropdown, TextField, Button, Box } from '@qbe/ads-core';
import { FormCard } from '@qbe/ads-blocks-forms';
import { useContentContext } from '@qbe/dfc-content';

import { InformationPopUp } from 'components';
import FormSpacer from 'components/FormSpacer/FormSpacer';
import CurrencyTextField from 'components/CurrencyTextField/CurrencyTextField';

import { employmentTypeList, EmploymentFormFields, EmploymentData } from 'contexts/FormProvider';

import validationSchema from '../schema';
import { useContentComplaintPartners } from 'utils/content';

interface Props {
  arrayHelpers: FieldArrayRenderProps;
  index: number;
}

const EmploymentEdit = React.forwardRef((props: Props, ref: React.Ref<HTMLDivElement>) => {
  const { arrayHelpers, index } = props;

  const { values } = useFormikContext<EmploymentFormFields>();

  const { getTextContent } = useContentComplaintPartners({
    context: useContentContext(),
  });

  const TEXT_CONTENT = {
    employmentHeading: getTextContent('fha.employment.details.employmentHeading'),
    employmentType: getTextContent('fha.employment.details.employmentType'),
    employmentTypeLabel: getTextContent('fha.employment.details.employmentType.label'),
    employerName: getTextContent('fha.employment.details.employerName'),
    employerNameLabel: getTextContent('fha.employment.details.employerName.label'),
    occupation: getTextContent('fha.employment.details.occupation'),
    occupationLabel: getTextContent('fha.employment.details.occupation.label'),
    contactPerson: getTextContent('fha.employment.details.contactPerson'),
    contactPersonLabel: getTextContent('fha.employment.details.contactPerson.label'),
    contactPersonNumber: getTextContent('fha.employment.details.contactPersonNumber'),
    contactPersonNumberLabel: getTextContent('fha.employment.details.contactPersonNumber.label'),
    salary: getTextContent('fha.employment.details.salary'),
    salaryLabel: getTextContent('fha.employment.details.salary.label'),
    salaryInfo: getTextContent('fha.employment.details.salaryInfo'),
    addEmployerLabel: getTextContent('fha.employment.details.addEmployer.label'),
    removeEmployerLabel: getTextContent('fha.employment.details.removeEmployer.label'),
  };

  const onAddEmploymentDetails = (idx: number) => {
    const newDetails: EmploymentData = {
      id: uniqueId(),
      employmentType: '',
      employerName: '',
      occupation: '',
      contactPerson: '',
      contactPersonNumber: '',
      salary: '',
    };

    arrayHelpers.insert(idx + 1, newDetails);
  };

  const isDisabled = !validationSchema.isValidSync(values);
  const isCurrentlyEmployed = values.employmentStatus === 'yes';
  const isFull = values.employmentData.length === 5;
  const hasOneElement = values.employmentData.length === 1;

  if (!isCurrentlyEmployed) return null;

  return (
    <>
      <FormSpacer type="pagesection" />
      <div ref={ref} />
      <FormCard variant="sectionTitle" title={`${TEXT_CONTENT.employmentHeading} - (${index + 1})`}>
        <FormGroupLabel alignCenter title={TEXT_CONTENT.employmentType} required />
        <FormSpacer type="input" />
        <Field name={`employmentData[${index}].employmentType`}>
          {({ field, meta }: FieldProps) => (
            <Dropdown
              {...field}
              id={field.name}
              label={TEXT_CONTENT.employmentTypeLabel}
              inputProps={
                {
                  'data-track': field.name,
                  'data-track-value': 'capture',
                } as any
              }
              optionList={employmentTypeList.map((label) => ({ label, value: label }))}
              error={meta.touched && !!meta.error}
            />
          )}
        </Field>

        <FormGroupLabel alignCenter title={TEXT_CONTENT.employerName} required />
        <FormSpacer type="input" />
        <Field name={`employmentData[${index}].employerName`}>
          {({ field, meta }: FieldProps) => (
            <TextField
              {...field}
              id={field.name}
              label={TEXT_CONTENT.employerNameLabel}
              data-track={field.name}
              data-track-value="mask"
              maxLength={200}
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
            />
          )}
        </Field>

        <FormGroupLabel alignCenter title={TEXT_CONTENT.occupation} required />
        <FormSpacer type="input" />
        <Field name={`employmentData[${index}].occupation`}>
          {({ field, meta }: FieldProps) => (
            <TextField
              {...field}
              id={field.name}
              label={TEXT_CONTENT.occupationLabel}
              data-track={field.name}
              data-track-value="mask"
              maxLength={200}
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
            />
          )}
        </Field>

        <FormGroupLabel alignCenter title={TEXT_CONTENT.contactPerson} />
        <FormSpacer type="input" />
        <Field name={`employmentData[${index}].contactPerson`}>
          {({ field, meta }: FieldProps) => (
            <TextField
              {...field}
              id={field.name}
              label={TEXT_CONTENT.contactPersonLabel}
              data-track={field.name}
              data-track-value="mask"
              maxLength={200}
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
            />
          )}
        </Field>

        <FormGroupLabel alignCenter title={TEXT_CONTENT.contactPersonNumber} />
        <FormSpacer type="input" />
        <Field name={`employmentData[${index}].contactPersonNumber`}>
          {({ field, meta }: FieldProps) => (
            <TextField
              {...field}
              id={field.name}
              label={TEXT_CONTENT.contactPersonNumberLabel}
              data-track={field.name}
              data-track-value="mask"
              maxLength={20}
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
            />
          )}
        </Field>

        <FormGroupLabel
          alignCenter
          title={TEXT_CONTENT.salary}
          titleSuffix={<InformationPopUp message={TEXT_CONTENT.salaryInfo} />}
          required
        />
        <FormSpacer type="input" />
        <Field name={`employmentData[${index}].salary`}>
          {({ field, meta }: FieldProps) => (
            <CurrencyTextField
              {...field}
              id={field.name}
              label={TEXT_CONTENT.salaryLabel}
              data-track={field.name}
              data-track-value="mask"
              maxLength={10}
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
            />
          )}
        </Field>

        <FormSpacer type="formgroup" />

        <Box display="flex" flexDirection="row" justifyContent="center">
          <Button
            type="button"
            label={TEXT_CONTENT.addEmployerLabel}
            variant="secondary-on-light"
            disabled={isFull || isDisabled}
            onClick={() => onAddEmploymentDetails(index)}
          />
          <FormSpacer type="input" direction="row" />
          <Button
            type="button"
            label={TEXT_CONTENT.removeEmployerLabel}
            variant="secondary-on-light"
            disabled={hasOneElement}
            onClick={() => arrayHelpers.remove(index)}
          />
        </Box>
      </FormCard>
    </>
  );
});

EmploymentEdit.displayName = 'EmploymentEdit';

export default EmploymentEdit;
